import type { ReactNode } from "react";
import classNames from "classnames";
import { CloseOutlined } from "@ant-design/icons";

export type BulkSelectionActionBarProps = {
  selectedCount: number;
  onClear: () => void;
  children?: ReactNode;
  className?: string;
  "data-testid"?: string;
};

/**
 * Full-width selection toolbar shell: close control + "{n} Selected" pill on
 * the left, and a flexible actions slot (`children`) on the right.
 */
export default function BulkSelectionActionBar({
  selectedCount,
  onClear,
  children,
  className,
  "data-testid": testId = "bulk-selection-action-bar"
}: BulkSelectionActionBarProps) {
  if (selectedCount < 1) return null;

  return (
    <div
      className={classNames(
        "flex w-full items-center justify-between gap-3 rounded-md bg-ciq-gray-100 px-4 py-2",
        className
      )}
      data-testid={testId}
    >
      <div className="flex shrink-0 items-center gap-3">
        <button
          type="button"
          aria-label="Clear selection"
          data-testid={`${testId}-clear`}
          className="flex cursor-pointer items-center justify-center border-0 bg-transparent p-1 text-ciq-primary-900"
          onClick={onClear}
        >
          <CloseOutlined />
        </button>
        <span
          className="rounded-full border border-solid border-ciq-gray-300 bg-white px-3 py-1 text-sm text-ciq-primary-900"
          data-testid={`${testId}-count`}
        >
          {selectedCount} Selected
        </span>
      </div>

      <div
        className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2"
        data-testid={`${testId}-actions`}
      >
        {children}
      </div>
    </div>
  );
}
